import React, { useEffect, useState, useCallback, useMemo } from "react";
import { parsedUser } from "../../common/GetCurrentUser";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
  Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import API from "../../common/API";
import moment from "moment";
import { FormatMoney } from "../../common/helpers";
import { Container, Row, Col } from "react-bootstrap";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ShowcaseSlide {
  id: string;
  title: string;
  type: "line" | "bar" | "doughnut" | "kpi";
  data: any;
  options?: object;
}

const ShowcaseDashboard: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const [isAutoRotating, setIsAutoRotating] = useState<boolean>(true);
  const [rotationInterval, setRotationInterval] = useState<number>(30);

  // Data states
  const [generalInfo, setGeneralInfo] = useState<any>(null);
  const [summaryByStage, setSummaryByStage] = useState<any[]>([]);
  const [summaryByStatus, setSummaryByStatus] = useState<any[]>([]);
  const [summaryByCategory, setSummaryByCategory] = useState<any[]>([]);
  const [visitsByMonth, setVisitsByMonth] = useState<any[]>([]);
  const [placesByMonth, setPlacesByMonth] = useState<any[]>([]);
  const [vehicleByStatus, setVehicleByStatus] = useState<any[]>([]);
  const [vehicleByBrand, setVehicleByBrand] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const user = parsedUser();

  // Fetch all data in parallel
  const fetchAllData = useCallback(async () => {
    if (!user?.OrganizationId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const orgId = user.OrganizationId;

    try {
      const [
        generalRes,
        stageRes,
        statusRes,
        categoryRes,
        visitsRes,
        placesRes,
        vehicleStatusRes,
        vehicleBrandRes,
      ] = await Promise.all([
        API.getAction(`dashboard/general?organizationId=${orgId}`).catch(
          () => ({ data: { response: null } })
        ),
        API.getAction(`place/summaryByStage?organizationId=${orgId}`).catch(
          () => ({ data: { response: [] } })
        ),
        API.getAction(`place/summaryByStatus?organizationId=${orgId}`).catch(
          () => ({ data: { response: [] } })
        ),
        API.getAction(`place/SummaryByCategory?organizationId=${orgId}`).catch(
          () => ({ data: { response: [] } })
        ),
        API.getAction(`dashboard/VisitsByMonth?organizationId=${orgId}`).catch(
          () => ({ data: { response: [] } })
        ),
        API.getAction(`dashboard/PlaceByMonth?organizationId=${orgId}`).catch(
          () => ({ data: { response: [] } })
        ),
        API.getAction(`agl/VehicleByStatus?organizationId=${orgId}`).catch(
          () => ({ data: { response: [] } })
        ),
        API.getAction(`agl/VehicleByBrand?organizationId=${orgId}`).catch(
          () => ({ data: { response: [] } })
        ),
      ]);

      setGeneralInfo(generalRes.data?.response || null);
      setSummaryByStage(
        Array.isArray(stageRes.data?.response) ? stageRes.data.response : []
      );
      setSummaryByStatus(
        Array.isArray(statusRes.data?.response) ? statusRes.data.response : []
      );
      setSummaryByCategory(
        Array.isArray(categoryRes.data?.response)
          ? categoryRes.data.response
          : []
      );
      setVisitsByMonth(
        Array.isArray(visitsRes.data?.response) ? visitsRes.data.response : []
      );
      setPlacesByMonth(
        Array.isArray(placesRes.data?.response) ? placesRes.data.response : []
      );
      setVehicleByStatus(
        Array.isArray(vehicleStatusRes.data?.response)
          ? vehicleStatusRes.data.response
          : []
      );
      setVehicleByBrand(
        Array.isArray(vehicleBrandRes.data?.response)
          ? vehicleBrandRes.data.response
          : []
      );
    } catch (error) {
      console.error("Error fetching showcase data:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.OrganizationId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) setLoading(false);
    }, 8000);
    return () => clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    if (user?.OrganizationId) {
      fetchAllData();
    } else {
      setLoading(false);
    }
  }, [user?.OrganizationId, fetchAllData]);

  // Theme colors
  const themeColors = useMemo(
    () => ({
      background: isDarkMode ? "#0f172a" : "#f1f5f9",
      cardBackground: isDarkMode ? "#1e293b" : "#ffffff",
      text: isDarkMode ? "#f1f5f9" : "#0f172a",
      secondaryText: isDarkMode ? "#94a3b8" : "#64748b",
      accent: "#6366f1",
      success: "#10b981",
      warning: "#f59e0b",
      danger: "#ef4444",
      info: "#3b82f6",
      purple: "#8b5cf6",
      pink: "#ec4899",
      chartColors: isDarkMode
        ? [
          "#6366f1",
          "#10b981",
          "#f59e0b",
          "#ef4444",
          "#8b5cf6",
          "#ec4899",
          "#06b6d4",
          "#f97316",
        ]
        : [
          "#4f46e5",
          "#059669",
          "#d97706",
          "#dc2626",
          "#7c3aed",
          "#db2777",
          "#0891b2",
          "#ea580c",
        ],
    }),
    [isDarkMode]
  );

  // Chart options factory
  const getChartOptions = useCallback(
    (title: string, showGrid = true) => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top" as const,
          labels: {
            color: themeColors.text,
            font: { size: 14, weight: "bold" as const },
            padding: 20,
          },
        },
        title: {
          display: true,
          text: title,
          color: themeColors.text,
          font: { size: 22, weight: "bold" as const },
          padding: { bottom: 20 },
        },
      },
      scales: showGrid
        ? {
          x: {
            ticks: {
              color: themeColors.secondaryText,
              font: { size: 12 },
            },
            grid: {
              color: isDarkMode
                ? "rgba(148,163,184,0.1)"
                : "rgba(0,0,0,0.06)",
            },
          },
          y: {
            ticks: {
              color: themeColors.secondaryText,
              font: { size: 12 },
            },
            grid: {
              color: isDarkMode
                ? "rgba(148,163,184,0.1)"
                : "rgba(0,0,0,0.06)",
            },
          },
        }
        : undefined,
    }),
    [themeColors, isDarkMode]
  );

  // ============ BUILD SLIDES ============
  const slides: ShowcaseSlide[] = useMemo(() => {
    const slideList: ShowcaseSlide[] = [];

    // SLIDE 1: KPI Summary Cards (always first)
    slideList.push({
      id: "kpi-summary",
      title: "Indicadores Clave",
      type: "kpi",
      data: null,
    });

    // SLIDE 2: Visits by Month (Line chart)
    if (visitsByMonth.length > 0) {
      slideList.push({
        id: "visits-monthly",
        title: "Visitas Mensuales",
        type: "line",
        data: {
          labels: visitsByMonth.map((item: any) =>
            moment(item.month).format("MMM YYYY")
          ),
          datasets: [
            {
              label: "Visitas",
              data: visitsByMonth.map((item: any) => item.count),
              borderColor: themeColors.chartColors[0],
              backgroundColor: themeColors.chartColors[0] + "20",
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointBackgroundColor: themeColors.chartColors[0],
              pointBorderColor: isDarkMode ? "#1e293b" : "#ffffff",
              pointBorderWidth: 3,
              pointRadius: 6,
              pointHoverRadius: 10,
            },
          ],
        },
        options: getChartOptions(
          `Visitas Mensuales — Total: ${FormatMoney(
            visitsByMonth.reduce(
              (sum: number, item: any) => sum + (item.count || 0),
              0
            ),
            0
          )}`
        ),
      });
    }

    // SLIDE 3: New Places by Month (Bar chart)
    if (placesByMonth.length > 0) {
      slideList.push({
        id: "places-monthly",
        title: "Nuevos Lugares por Mes",
        type: "bar",
        data: {
          labels: placesByMonth.map((item: any) =>
            moment(item.month).format("MMM YYYY")
          ),
          datasets: [
            {
              label: "Nuevos Lugares",
              data: placesByMonth.map((item: any) => item.count),
              backgroundColor: themeColors.chartColors[1] + "80",
              borderColor: themeColors.chartColors[1],
              borderWidth: 2,
              borderRadius: 6,
            },
          ],
        },
        options: getChartOptions(
          `Nuevos Lugares — Total: ${FormatMoney(
            placesByMonth.reduce(
              (sum: number, item: any) => sum + (item.count || 0),
              0
            ),
            0
          )}`
        ),
      });
    }

    // SLIDE 4: Visits vs New Places (combined bar chart)
    if (visitsByMonth.length > 0 && placesByMonth.length > 0) {
      // Build a merged timeline
      const dateMap = new Map<string, any>();

      visitsByMonth.forEach((item: any) => {
        const key = moment(item.month).format("YYYY-MM");
        if (!dateMap.has(key)) {
          dateMap.set(key, {
            date: key,
            label: moment(item.month).format("MMM YYYY"),
            visits: 0,
            places: 0,
          });
        }
        dateMap.get(key)!.visits = item.count || 0;
      });

      placesByMonth.forEach((item: any) => {
        const key = moment(item.month).format("YYYY-MM");
        if (!dateMap.has(key)) {
          dateMap.set(key, {
            date: key,
            label: moment(item.month).format("MMM YYYY"),
            visits: 0,
            places: 0,
          });
        }
        dateMap.get(key)!.places = item.count || 0;
      });

      const merged = Array.from(dateMap.values()).sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      slideList.push({
        id: "visits-vs-places",
        title: "Visitas vs Nuevos Lugares",
        type: "bar",
        data: {
          labels: merged.map((item) => item.label),
          datasets: [
            {
              label: "Visitas",
              data: merged.map((item) => item.visits),
              backgroundColor: themeColors.chartColors[0] + "80",
              borderColor: themeColors.chartColors[0],
              borderWidth: 2,
              borderRadius: 4,
            },
            {
              label: "Nuevos Lugares",
              data: merged.map((item) => item.places),
              backgroundColor: themeColors.chartColors[1] + "80",
              borderColor: themeColors.chartColors[1],
              borderWidth: 2,
              borderRadius: 4,
            },
          ],
        },
        options: getChartOptions("Visitas vs Nuevos Lugares"),
      });
    }

    // SLIDE 5: Places by Stage (Doughnut)
    if (summaryByStage.length > 0) {
      slideList.push({
        id: "places-by-stage",
        title: "Distribución por Etapa",
        type: "doughnut",
        data: {
          labels: summaryByStage.map((item: any) => item.name),
          datasets: [
            {
              data: summaryByStage.map((item: any) => item.count),
              backgroundColor: summaryByStage.map(
                (_: any, i: number) =>
                  themeColors.chartColors[i % themeColors.chartColors.length]
              ),
              borderColor: isDarkMode ? "#1e293b" : "#ffffff",
              borderWidth: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "right" as const,
              labels: {
                color: themeColors.text,
                font: { size: 14, weight: "bold" as const },
                padding: 16,
              },
            },
            title: {
              display: true,
              text: `Lugares por Etapa — Total: ${FormatMoney(
                summaryByStage.reduce(
                  (sum: number, item: any) => sum + (item.count || 0),
                  0
                ),
                0
              )}`,
              color: themeColors.text,
              font: { size: 22, weight: "bold" as const },
              padding: { bottom: 20 },
            },
          },
        },
      });
    }

    // SLIDE 6: Places by Status (Doughnut)
    if (summaryByStatus.length > 0) {
      slideList.push({
        id: "places-by-status",
        title: "Distribución por Estatus",
        type: "doughnut",
        data: {
          labels: summaryByStatus.map((item: any) => item.name),
          datasets: [
            {
              data: summaryByStatus.map((item: any) => item.count),
              backgroundColor: summaryByStatus.map(
                (item: any, i: number) =>
                  item.color ||
                  themeColors.chartColors[i % themeColors.chartColors.length]
              ),
              borderColor: isDarkMode ? "#1e293b" : "#ffffff",
              borderWidth: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "right" as const,
              labels: {
                color: themeColors.text,
                font: { size: 14, weight: "bold" as const },
                padding: 16,
              },
            },
            title: {
              display: true,
              text: `Lugares por Estatus — Total: ${FormatMoney(
                summaryByStatus.reduce(
                  (sum: number, item: any) => sum + (item.count || 0),
                  0
                ),
                0
              )}`,
              color: themeColors.text,
              font: { size: 22, weight: "bold" as const },
              padding: { bottom: 20 },
            },
          },
        },
      });
    }

    // SLIDE 7: Places by Category (Horizontal Bar)
    if (summaryByCategory.length > 0) {
      slideList.push({
        id: "places-by-category",
        title: "Lugares por Categoría",
        type: "bar",
        data: {
          labels: summaryByCategory.map((item: any) => item.name),
          datasets: [
            {
              label: "Lugares",
              data: summaryByCategory.map((item: any) => item.count),
              backgroundColor: summaryByCategory.map(
                (_: any, i: number) =>
                  themeColors.chartColors[i % themeColors.chartColors.length] +
                  "80"
              ),
              borderColor: summaryByCategory.map(
                (_: any, i: number) =>
                  themeColors.chartColors[i % themeColors.chartColors.length]
              ),
              borderWidth: 2,
              borderRadius: 6,
            },
          ],
        },
        options: {
          ...getChartOptions(
            `Lugares por Categoría — Total: ${FormatMoney(
              summaryByCategory.reduce(
                (sum: number, item: any) => sum + (item.count || 0),
                0
              ),
              0
            )}`
          ),
          indexAxis: "y" as const,
        },
      });
    }

    // SLIDE 8: Vehicle Status (Doughnut) — if data exists
    if (vehicleByStatus.length > 0) {
      const statusLabels: Record<number, string> = {
        4: "En Movimiento",
        1: "Detenido",
        3: "Inactivo/Parqueado",
      };
      const statusColors: Record<number, string> = {
        4: themeColors.success,
        1: themeColors.danger,
        3: themeColors.warning,
      };

      slideList.push({
        id: "vehicle-status",
        title: "Estado de Vehículos",
        type: "doughnut",
        data: {
          labels: vehicleByStatus.map(
            (item: any) => statusLabels[item.status] || `Status ${item.status}`
          ),
          datasets: [
            {
              data: vehicleByStatus.map((item: any) => item.count),
              backgroundColor: vehicleByStatus.map(
                (item: any) =>
                  statusColors[item.status] || themeColors.secondaryText
              ),
              borderColor: isDarkMode ? "#1e293b" : "#ffffff",
              borderWidth: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom" as const,
              labels: {
                color: themeColors.text,
                font: { size: 16, weight: "bold" as const },
                padding: 24,
              },
            },
            title: {
              display: true,
              text: `Vehículos — Total: ${FormatMoney(
                vehicleByStatus.reduce(
                  (sum: number, item: any) => sum + (item.count || 0),
                  0
                ),
                0
              )}`,
              color: themeColors.text,
              font: { size: 22, weight: "bold" as const },
              padding: { bottom: 20 },
            },
          },
        },
      });
    }

    // SLIDE 9: Vehicle by Brand (Horizontal Bar)
    if (vehicleByBrand.length > 0) {
      slideList.push({
        id: "vehicle-by-brand",
        title: "Vehículos por Marca",
        type: "bar",
        data: {
          labels: vehicleByBrand.map((item: any) => item.brand || "N/A"),
          datasets: [
            {
              label: "Vehículos",
              data: vehicleByBrand.map((item: any) => item.count),
              backgroundColor: vehicleByBrand.map(
                (_: any, i: number) =>
                  themeColors.chartColors[i % themeColors.chartColors.length] +
                  "80"
              ),
              borderColor: vehicleByBrand.map(
                (_: any, i: number) =>
                  themeColors.chartColors[i % themeColors.chartColors.length]
              ),
              borderWidth: 2,
              borderRadius: 6,
            },
          ],
        },
        options: {
          ...getChartOptions(
            `Vehículos por Marca — Total: ${FormatMoney(
              vehicleByBrand.reduce(
                (sum: number, item: any) => sum + (item.count || 0),
                0
              ),
              0
            )}`
          ),
          indexAxis: "y" as const,
        },
      });
    }

    return slideList;
  }, [
    generalInfo,
    summaryByStage,
    summaryByStatus,
    summaryByCategory,
    visitsByMonth,
    placesByMonth,
    vehicleByStatus,
    vehicleByBrand,
    themeColors,
    getChartOptions,
    isDarkMode,
  ]);

  // Auto-rotation
  useEffect(() => {
    if (!isAutoRotating || slides.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
    }, rotationInterval * 1000);

    return () => clearInterval(interval);
  }, [isAutoRotating, slides.length, rotationInterval]);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Navigation
  const goToSlide = (index: number) => {
    setCurrentSlideIndex(index);
    setIsAutoRotating(false);
  };

  const nextSlide = () => {
    setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlideIndex(
      (prev) => (prev - 1 + slides.length) % slides.length
    );
  };

  // ============ KPI SUMMARY PANEL ============
  const renderKPISummary = () => {
    const totalPlaces = generalInfo?.totalPlaces || 0;
    const totalVehicles = generalInfo?.totalVehicles || 0;
    const totalAgents = generalInfo?.totalUsers || 0;
    const totalVisits = generalInfo?.totalVisits || 0;

    // Calculated metrics
    const visitsPerAgent =
      totalAgents > 0 ? Math.round(totalVisits / totalAgents) : 0;
    const placesPerAgent =
      totalAgents > 0 ? Math.round(totalPlaces / totalAgents) : 0;
    const visitsPerPlace =
      totalPlaces > 0 ? (totalVisits / totalPlaces).toFixed(1) : "0";

    // Stage/status totals
    const totalByStage = summaryByStage.reduce(
      (sum, item) => sum + (item.count || 0),
      0
    );
    const totalByStatus = summaryByStatus.reduce(
      (sum, item) => sum + (item.count || 0),
      0
    );

    // Vehicle totals
    const vehiclesMoving =
      vehicleByStatus.find((v: any) => v.status === 4)?.count || 0;
    const vehiclesStopped =
      vehicleByStatus.find((v: any) => v.status === 1)?.count || 0;
    const vehiclesIdle =
      vehicleByStatus.find((v: any) => v.status === 3)?.count || 0;

    return (
      <div className="h-100 d-flex flex-column justify-content-center p-4">
        <h1
          className="text-center mb-5"
          style={{
            color: themeColors.text,
            fontSize: "2.8rem",
            fontWeight: 800,
            letterSpacing: "-0.02em",
          }}
        >
          <i className="fas fa-chart-pie me-3" style={{ color: themeColors.accent }}></i>
          ShearlyBI — Indicadores Clave
        </h1>

        {/* Row 1: Main KPIs */}
        <Row className="g-4 mb-4">
          <Col xs={6} lg={3}>
            <div
              className="p-4 rounded-4 text-center h-100 d-flex flex-column justify-content-center"
              style={{
                backgroundColor: themeColors.cardBackground,
                border: `2px solid ${themeColors.accent}`,
                boxShadow: `0 4px 20px ${themeColors.accent}20`,
              }}
            >
              <i
                className="fas fa-map-marker-alt mb-3"
                style={{ fontSize: "2.5rem", color: themeColors.accent }}
              ></i>
              <h2
                style={{
                  color: themeColors.text,
                  fontSize: "3rem",
                  fontWeight: 800,
                }}
              >
                {FormatMoney(totalPlaces, 0)}
              </h2>
              <p
                style={{
                  color: themeColors.secondaryText,
                  fontSize: "1.1rem",
                  marginBottom: 0,
                  fontWeight: 600,
                }}
              >
                Lugares Totales
              </p>
            </div>
          </Col>
          <Col xs={6} lg={3}>
            <div
              className="p-4 rounded-4 text-center h-100 d-flex flex-column justify-content-center"
              style={{
                backgroundColor: themeColors.cardBackground,
                border: `2px solid ${themeColors.success}`,
                boxShadow: `0 4px 20px ${themeColors.success}20`,
              }}
            >
              <i
                className="fas fa-calendar-check mb-3"
                style={{ fontSize: "2.5rem", color: themeColors.success }}
              ></i>
              <h2
                style={{
                  color: themeColors.text,
                  fontSize: "3rem",
                  fontWeight: 800,
                }}
              >
                {FormatMoney(totalVisits, 0)}
              </h2>
              <p
                style={{
                  color: themeColors.secondaryText,
                  fontSize: "1.1rem",
                  marginBottom: 0,
                  fontWeight: 600,
                }}
              >
                Visitas Totales
              </p>
            </div>
          </Col>
          <Col xs={6} lg={3}>
            <div
              className="p-4 rounded-4 text-center h-100 d-flex flex-column justify-content-center"
              style={{
                backgroundColor: themeColors.cardBackground,
                border: `2px solid ${themeColors.info}`,
                boxShadow: `0 4px 20px ${themeColors.info}20`,
              }}
            >
              <i
                className="fas fa-user-tie mb-3"
                style={{ fontSize: "2.5rem", color: themeColors.info }}
              ></i>
              <h2
                style={{
                  color: themeColors.text,
                  fontSize: "3rem",
                  fontWeight: 800,
                }}
              >
                {FormatMoney(totalAgents, 0)}
              </h2>
              <p
                style={{
                  color: themeColors.secondaryText,
                  fontSize: "1.1rem",
                  marginBottom: 0,
                  fontWeight: 600,
                }}
              >
                Agentes
              </p>
            </div>
          </Col>
          <Col xs={6} lg={3}>
            <div
              className="p-4 rounded-4 text-center h-100 d-flex flex-column justify-content-center"
              style={{
                backgroundColor: themeColors.cardBackground,
                border: `2px solid ${themeColors.warning}`,
                boxShadow: `0 4px 20px ${themeColors.warning}20`,
              }}
            >
              <i
                className="fas fa-car mb-3"
                style={{ fontSize: "2.5rem", color: themeColors.warning }}
              ></i>
              <h2
                style={{
                  color: themeColors.text,
                  fontSize: "3rem",
                  fontWeight: 800,
                }}
              >
                {FormatMoney(totalVehicles, 0)}
              </h2>
              <p
                style={{
                  color: themeColors.secondaryText,
                  fontSize: "1.1rem",
                  marginBottom: 0,
                  fontWeight: 600,
                }}
              >
                Vehículos
              </p>
            </div>
          </Col>
        </Row>

        {/* Row 2: Productivity KPIs */}
        <Row className="g-4 mb-4">
          <Col xs={6} lg={4}>
            <div
              className="p-4 rounded-4 text-center"
              style={{
                backgroundColor: themeColors.cardBackground,
                border: `2px solid ${themeColors.purple}`,
                boxShadow: `0 4px 20px ${themeColors.purple}20`,
              }}
            >
              <i
                className="fas fa-tasks mb-2"
                style={{ fontSize: "2rem", color: themeColors.purple }}
              ></i>
              <h3
                style={{
                  color: themeColors.text,
                  fontSize: "2.2rem",
                  fontWeight: 800,
                }}
              >
                {visitsPerAgent}
              </h3>
              <p
                style={{
                  color: themeColors.secondaryText,
                  fontSize: "1rem",
                  marginBottom: 0,
                }}
              >
                Visitas por Agente
              </p>
            </div>
          </Col>
          <Col xs={6} lg={4}>
            <div
              className="p-4 rounded-4 text-center"
              style={{
                backgroundColor: themeColors.cardBackground,
                border: `2px solid ${themeColors.pink}`,
                boxShadow: `0 4px 20px ${themeColors.pink}20`,
              }}
            >
              <i
                className="fas fa-building mb-2"
                style={{ fontSize: "2rem", color: themeColors.pink }}
              ></i>
              <h3
                style={{
                  color: themeColors.text,
                  fontSize: "2.2rem",
                  fontWeight: 800,
                }}
              >
                {placesPerAgent}
              </h3>
              <p
                style={{
                  color: themeColors.secondaryText,
                  fontSize: "1rem",
                  marginBottom: 0,
                }}
              >
                Lugares por Agente
              </p>
            </div>
          </Col>
          <Col xs={6} lg={4}>
            <div
              className="p-4 rounded-4 text-center"
              style={{
                backgroundColor: themeColors.cardBackground,
                border: `2px solid ${themeColors.info}`,
                boxShadow: `0 4px 20px ${themeColors.info}20`,
              }}
            >
              <i
                className="fas fa-redo mb-2"
                style={{ fontSize: "2rem", color: themeColors.info }}
              ></i>
              <h3
                style={{
                  color: themeColors.text,
                  fontSize: "2.2rem",
                  fontWeight: 800,
                }}
              >
                {visitsPerPlace}
              </h3>
              <p
                style={{
                  color: themeColors.secondaryText,
                  fontSize: "1rem",
                  marginBottom: 0,
                }}
              >
                Visitas por Lugar
              </p>
            </div>
          </Col>
        </Row>

        {/* Row 3: Vehicle Status Summary */}
        {vehicleByStatus.length > 0 && (
          <Row className="g-4">
            <Col xs={4}>
              <div
                className="p-3 rounded-4 text-center"
                style={{
                  backgroundColor: themeColors.cardBackground,
                  border: `2px solid ${themeColors.success}`,
                }}
              >
                <i
                  className="fas fa-truck-moving mb-2"
                  style={{ fontSize: "1.8rem", color: themeColors.success }}
                ></i>
                <h4
                  style={{
                    color: themeColors.text,
                    fontSize: "1.8rem",
                    fontWeight: 800,
                  }}
                >
                  {vehiclesMoving}
                </h4>
                <small style={{ color: themeColors.secondaryText, fontWeight: 600 }}>
                  En Movimiento
                </small>
              </div>
            </Col>
            <Col xs={4}>
              <div
                className="p-3 rounded-4 text-center"
                style={{
                  backgroundColor: themeColors.cardBackground,
                  border: `2px solid ${themeColors.danger}`,
                }}
              >
                <i
                  className="fas fa-hand-paper mb-2"
                  style={{ fontSize: "1.8rem", color: themeColors.danger }}
                ></i>
                <h4
                  style={{
                    color: themeColors.text,
                    fontSize: "1.8rem",
                    fontWeight: 800,
                  }}
                >
                  {vehiclesStopped}
                </h4>
                <small style={{ color: themeColors.secondaryText, fontWeight: 600 }}>
                  Detenidos
                </small>
              </div>
            </Col>
            <Col xs={4}>
              <div
                className="p-3 rounded-4 text-center"
                style={{
                  backgroundColor: themeColors.cardBackground,
                  border: `2px solid ${themeColors.warning}`,
                }}
              >
                <i
                  className="fas fa-parking mb-2"
                  style={{ fontSize: "1.8rem", color: themeColors.warning }}
                ></i>
                <h4
                  style={{
                    color: themeColors.text,
                    fontSize: "1.8rem",
                    fontWeight: 800,
                  }}
                >
                  {vehiclesIdle}
                </h4>
                <small style={{ color: themeColors.secondaryText, fontWeight: 600 }}>
                  Inactivos
                </small>
              </div>
            </Col>
          </Row>
        )}
      </div>
    );
  };

  // ============ RENDER CHART ============
  const renderChart = (slide: ShowcaseSlide) => {
    if (slide.type === "kpi") return renderKPISummary();

    const ChartComponent = {
      line: Line,
      bar: Bar,
      doughnut: Doughnut,
    }[slide.type];

    return (
      <div className="h-100 p-4 d-flex flex-column">
        <div style={{ flex: 1, position: "relative" }}>
          <ChartComponent data={slide.data} options={slide.options as any} />
        </div>
      </div>
    );
  };

  // ============ LOADING STATE ============
  if (loading && slides.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{ backgroundColor: themeColors.background }}
      >
        <div className="text-center">
          <div
            className="spinner-border mb-3"
            style={{
              width: "4rem",
              height: "4rem",
              color: themeColors.accent,
            }}
          ></div>
          <h3 style={{ color: themeColors.text }}>
            Cargando Dashboard...
          </h3>
          <p style={{ color: themeColors.secondaryText }}>
            Recopilando datos de la organización
          </p>
        </div>
      </div>
    );
  }

  // ============ NO DATA STATE ============
  if (slides.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{ backgroundColor: themeColors.background }}
      >
        <div className="text-center p-4">
          <i
            className="fas fa-exclamation-circle mb-3"
            style={{ fontSize: "4rem", color: themeColors.accent }}
          ></i>
          <h3 style={{ color: themeColors.text }}>
            Sin datos disponibles
          </h3>
          <p style={{ color: themeColors.secondaryText }} className="mb-3">
            No se pudieron cargar los datos del dashboard
          </p>
          <button
            onClick={() => {
              setLoading(true);
              fetchAllData();
            }}
            className="btn btn-primary mt-3"
            style={{
              backgroundColor: themeColors.accent,
              borderColor: themeColors.accent,
            }}
          >
            <i className="fas fa-sync-alt me-2"></i> Reintentar
          </button>
        </div>
      </div>
    );
  }

  // ============ MAIN RENDER ============
  return (
    <div
      className="showcase-dashboard"
      style={{
        backgroundColor: themeColors.background,
        minHeight: "100vh",
        transition: "all 0.3s ease",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      {/* Control Bar */}
      <div
        className="d-flex justify-content-between align-items-center px-4 py-2"
        style={{
          backgroundColor: themeColors.cardBackground,
          borderBottom: `1px solid ${isDarkMode ? "rgba(148,163,184,0.15)" : "#e2e8f0"}`,
          backdropFilter: "blur(10px)",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <h5
            className="m-0"
            style={{ color: themeColors.text, fontWeight: 700 }}
          >
            <i
              className="fas fa-chart-pie me-2"
              style={{ color: themeColors.accent }}
            ></i>
            ShearlyBI
          </h5>
          <span
            className="badge"
            style={{
              backgroundColor: themeColors.accent + "20",
              color: themeColors.accent,
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            {currentSlideIndex + 1} / {slides.length}
          </span>
          <span
            style={{
              color: themeColors.secondaryText,
              fontSize: "0.85rem",
            }}
          >
            {slides[currentSlideIndex]?.title}
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          {/* Slide Indicators */}
          <div className="d-flex gap-1 me-2">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => goToSlide(index)}
                className="btn btn-sm rounded-circle p-0"
                title={slide.title}
                style={{
                  width: index === currentSlideIndex ? "24px" : "10px",
                  height: "10px",
                  borderRadius:
                    index === currentSlideIndex ? "5px" : "50%",
                  backgroundColor:
                    index === currentSlideIndex
                      ? themeColors.accent
                      : themeColors.secondaryText + "40",
                  border: "none",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </div>

          {/* Rotation Interval */}
          <select
            value={rotationInterval}
            onChange={(e) =>
              setRotationInterval(Number(e.target.value))
            }
            className="form-select form-select-sm"
            style={{
              width: "75px",
              backgroundColor: themeColors.cardBackground,
              color: themeColors.text,
              border: `1px solid ${isDarkMode ? "rgba(148,163,184,0.2)" : "#cbd5e1"}`,
              fontSize: "0.8rem",
            }}
          >
            <option value={10}>10s</option>
            <option value={15}>15s</option>
            <option value={30}>30s</option>
            <option value={60}>60s</option>
          </select>

          {/* Auto Rotate */}
          <button
            onClick={() => setIsAutoRotating(!isAutoRotating)}
            className="btn btn-sm"
            title={
              isAutoRotating ? "Pausar rotación" : "Iniciar rotación"
            }
            style={{
              backgroundColor: isAutoRotating
                ? themeColors.success
                : "transparent",
              color: isAutoRotating
                ? "#fff"
                : themeColors.secondaryText,
              border: isAutoRotating
                ? "none"
                : `1px solid ${themeColors.secondaryText}40`,
            }}
          >
            <i
              className={`fas fa-${isAutoRotating ? "pause" : "play"}`}
            ></i>
          </button>

          {/* Theme Toggle */}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="btn btn-sm"
            title={isDarkMode ? "Modo Claro" : "Modo Oscuro"}
            style={{
              backgroundColor: "transparent",
              color: themeColors.secondaryText,
              border: `1px solid ${themeColors.secondaryText}40`,
            }}
          >
            <i className={`fas fa-${isDarkMode ? "sun" : "moon"}`}></i>
          </button>

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            className="btn btn-sm"
            title={
              isFullscreen
                ? "Salir Pantalla Completa"
                : "Pantalla Completa"
            }
            style={{
              backgroundColor: "transparent",
              color: themeColors.accent,
              border: `1px solid ${themeColors.accent}40`,
            }}
          >
            <i
              className={`fas fa-${isFullscreen ? "compress" : "expand"}`}
            ></i>
          </button>

          {/* Refresh */}
          <button
            onClick={() => {
              setLoading(true);
              fetchAllData();
            }}
            className="btn btn-sm"
            title="Actualizar datos"
            style={{
              backgroundColor: "transparent",
              color: themeColors.secondaryText,
              border: `1px solid ${themeColors.secondaryText}40`,
            }}
          >
            <i className="fas fa-sync-alt"></i>
          </button>

          {/* Navigation */}
          <div className="btn-group ms-1">
            <button
              onClick={prevSlide}
              className="btn btn-sm"
              style={{
                backgroundColor: themeColors.accent + "20",
                color: themeColors.accent,
                border: "none",
              }}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button
              onClick={nextSlide}
              className="btn btn-sm"
              style={{
                backgroundColor: themeColors.accent + "20",
                color: themeColors.accent,
                border: "none",
              }}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Main Chart Content */}
      <Container
        fluid
        className="p-0"
        style={{ height: "calc(100vh - 56px)" }}
      >
        {slides.length > 0 && slides[currentSlideIndex] && (
          <div className="h-100 p-3">
            <div
              className="h-100 rounded-4 overflow-hidden"
              style={{
                backgroundColor: themeColors.cardBackground,
                boxShadow: isDarkMode
                  ? "0 8px 40px rgba(0,0,0,0.4)"
                  : "0 8px 40px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
              }}
            >
              {renderChart(slides[currentSlideIndex])}
            </div>
          </div>
        )}
      </Container>

      {/* Footer */}
      <div
        className="position-fixed bottom-0 start-0 end-0 px-4 py-2 text-center"
        style={{
          backgroundColor: themeColors.cardBackground,
          borderTop: `1px solid ${isDarkMode ? "rgba(148,163,184,0.15)" : "#e2e8f0"}`,
          backdropFilter: "blur(10px)",
        }}
      >
        <small style={{ color: themeColors.secondaryText }}>
          Última actualización: {moment().format("DD/MM/YYYY HH:mm")} |{" "}
          <strong>{slides[currentSlideIndex]?.title}</strong> | ShearlyBI
          Dashboard
        </small>
      </div>
    </div>
  );
};

export default ShowcaseDashboard;
